import React from "react";
import { Typography } from "@mui/material";
import type { JsonArray, JsonObject } from "type-fest";
import * as z from "zod";
import { getFieldLabel } from "~/domain/common";
import type {
  ApiVersion,
  DeprecationPolicy,
  VersionHistories,
  VersionHistory,
} from "~/domain/versioning";
import { DeprecableLabel } from "~/domain/versioning";
import {
  Boolean,
  Bytes,
  Datetime,
  Duration,
  formatNumber,
  formatPercent,
  Timestamp,
} from "~/format";
import { invariant } from "~/lib/invariant";
import { assertNever } from "~/utils";
import { CopyButton } from "../CopyableText";
import type { ForeignResourceRenderer } from "../Table";
import { JsonField } from "./JsonField";
import { Pre } from "./Pre";
import type {
  AccessorResourceField,
  NormalizedResourceField,
  ResourceField,
} from "./types";

const uuidSchema = z.string().uuid();

const numberSchema = z.number();

const bigintSchema = z.bigint();

const datetimeSchema = z.date();

const booleanSchema = z.boolean();

const jsonSchema = z
  .unknown()
  .refine((value): value is JsonObject | JsonArray => {
    return typeof value === "object";
  });

export function getNormalizedAvailableFields<
  TResource extends object,
  TForeignResource extends string,
>(
  fields: ReadonlyArray<ResourceField<TResource, TForeignResource>>,
  apiVersion: ApiVersion,
  deprecationPolicy: DeprecationPolicy,
  versionHistories: VersionHistories<any> | undefined,
): ReadonlyArray<NormalizedResourceField<TResource, TForeignResource>> {
  return fields.flatMap((field) => {
    if (versionHistories == null) {
      return field;
    } else {
      const fieldHistory: VersionHistory | undefined =
        versionHistories[field.accessor];

      if (fieldHistory == null) {
        return field;
      } else {
        const versionStatus = apiVersion.checkStatus(fieldHistory);

        if (versionStatus === "unavailable") {
          return [];
        } else if (
          deprecationPolicy === "hide" &&
          versionStatus === "deprecated"
        ) {
          return [];
        } else {
          return {
            ...field,
            deprecated: versionStatus === "deprecated",
          };
        }
      }
    }
  });
}

export function renderField<
  TResource extends object,
  TForeignResource extends string,
>(
  resource: TResource,
  field: NormalizedResourceField<TResource, TForeignResource>,
  renderForeignResource: ForeignResourceRenderer<TForeignResource> | undefined,
) {
  const label = field.label ?? getFieldLabel(field.accessor);
  const content = getAccessorFieldContent(
    resource[field.accessor],
    field,
    renderForeignResource,
  );

  return (
    <React.Fragment key={getFieldKey(field)}>
      <Typography
        component="dt"
        sx={{
          color: "text.secondary",
          fontWeight: "bold",
        }}
      >
        <DeprecableLabel deprecated={field.deprecated}>{label}</DeprecableLabel>
      </Typography>
      <Typography component="dd">{content}</Typography>
    </React.Fragment>
  );
}

function getAccessorFieldContent(
  fieldData: unknown,
  field: AccessorResourceField<any, any>,
  renderForeignResource: ForeignResourceRenderer<any> | undefined,
): React.ReactNode {
  if (fieldData == null) {
    return "-";
  }

  const { dataType } = field;
  switch (dataType) {
    case "text": {
      return String(fieldData);
    }
    case "id": {
      const value = String(fieldData);

      return (
        <span>
          {value} <CopyButton text={value} size="small" />
        </span>
      );
    }
    case "pre": {
      return <Pre>{String(fieldData)}</Pre>;
    }
    case "number": {
      return formatNumber(numberSchema.parse(fieldData));
    }
    case "percent": {
      return formatPercent(numberSchema.parse(fieldData));
    }
    case "timestamp": {
      return <Timestamp value={bigintSchema.parse(fieldData)} />;
    }
    case "bigint": {
      // TODO: Format this better
      return String(bigintSchema.parse(fieldData));
    }
    case "duration": {
      return <Duration value={bigintSchema.parse(fieldData)} />;
    }
    case "datetime": {
      return <Datetime date={datetimeSchema.parse(fieldData)} />;
    }
    case "bytes": {
      return <Bytes value={numberSchema.parse(fieldData)} />;
    }
    case "boolean": {
      return <Boolean value={booleanSchema.parse(fieldData)} />;
    }
    case "json": {
      return <JsonField value={jsonSchema.parse(fieldData)} />;
    }
    case "foreign-key": {
      invariant(
        renderForeignResource !== undefined,
        "No foreign resource renderer provided",
      );

      return renderForeignResource(
        uuidSchema.parse(fieldData),
        field.resourceType,
      );
    }
    default: {
      assertNever(dataType);
    }
  }
}

export function getFieldKey(field: ResourceField<any, any>): React.Key {
  return field.label ?? getFieldLabel(field.accessor);
}
